import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { stashReturnTo } from './modules/auth/postAuthReturn';
import { rememberHubVisit, takeHubVisit } from './modules/courses/pendingHub';
import { useAuth } from './modules/auth/contexts/AuthenticationContext';
import { useIsInstructor, useIsSystemAdmin, useProfile } from './modules/auth/hooks';
import { isProduction } from './shared/config/env';
import LoadingScreen from './shared/components/LoadingScreen';
import ErrorBoundary from './shared/components/ErrorBoundary';
import { lazyPage } from './shared/utils/lazyPage';

const LandingPage = lazyPage(() => import('./modules/auth/pages/LandingPage'));
const ClientLandingPage = lazyPage(() => import('./modules/auth/pages/ClientLandingPage'));
const TjxLandingPage = lazyPage(() => import('./modules/auth/pages/TjxLandingPage'));
const WalgreensLandingPage = lazyPage(() => import('./modules/auth/pages/WalgreensLandingPage'));
const DominosLandingPage = lazyPage(() => import('./modules/partners/pages/DominosLandingPage'));
const InvitePage = lazyPage(() => import('./modules/auth/pages/InvitePage'));
const InviteLinkPage = lazyPage(() => import('./modules/auth/pages/InviteLinkPage'));
const ProfilePage = lazyPage(() => import('./modules/auth/pages/ProfilePage'));
const CompleteProfilePage = lazyPage(() => import('./modules/auth/pages/CompleteProfilePage'));
const AcceptInvitationPage = lazyPage(() => import('./modules/auth/pages/AcceptInvitationPage'));
const DashboardPage = lazyPage(() => import('./modules/dashboard/pages/DashboardPage'));
const AdminDashboardPage = lazyPage(() => import('./modules/admin/pages/AdminDashboardPage'));
const CoursesPage = lazyPage(() => import('./modules/courses/pages/CoursesPage'));
const MyCoursesPage = lazyPage(() => import('./modules/courses/pages/MyCoursesPage'));
const LaunchCoursePage = lazyPage(() => import('./modules/courses/pages/LaunchCoursePage'));
const GetStartedPage = lazyPage(() => import('./modules/curriculum/pages/GetStartedPage'));
const BillingPage = lazyPage(() => import('./modules/billing/pages/BillingPage'));
const OrganizationPage = lazyPage(() => import('./modules/organization/pages/OrganizationPage'));
const GroupPackagesPage = lazyPage(() => import('./modules/organization/pages/GroupPackagesPage'));
const BuyGroupPackagePage = lazyPage(() => import('./modules/organization/pages/BuyGroupPackagePage'));
const EmployeeAnalyticsPage = lazyPage(() => import('./modules/organization/pages/EmployeeAnalyticsPage'));
const EventsPage = lazyPage(() => import('./modules/events/pages/EventsPage'));
const EventCheckoutPage = lazyPage(() => import('./modules/events/pages/EventCheckoutPage'));
const PracticePage = lazyPage(() => import('./modules/virtual_sapiens/pages/PracticePage'));
const EnrollmentAssessmentPage = lazyPage(() => import('./modules/virtual_sapiens/pages/EnrollmentAssessmentPage'));
const VsLaunchPage = lazyPage(() => import('./modules/virtual_sapiens/pages/VsLaunchPage'));
const RegistrationsPage = lazyPage(() => import('./modules/admin/pages/RegistrationsPage'));
const AdminUsersPage = lazyPage(() => import('./modules/admin/pages/AdminUsersPage'));
const AdminMarketingContentPage = lazyPage(() => import('./modules/admin/pages/AdminMarketingContentPage'));
const AdminPageContentEditorPage = lazyPage(() => import('./modules/admin/pages/AdminPageContentEditorPage'));
const AdminMarketingPageEditPage = lazyPage(() => import('./modules/admin/pages/AdminMarketingPageEditPage'));
const AdminPressReleaseEditPage = lazyPage(() => import('./modules/admin/pages/AdminPressReleaseEditPage'));
const AdminBlogPostEditPage = lazyPage(() => import('./modules/admin/pages/AdminBlogPostEditPage'));
const AdminPortfolioItemEditPage = lazyPage(() => import('./modules/admin/pages/AdminPortfolioItemEditPage'));
const AdminTestimonialEditPage = lazyPage(() => import('./modules/admin/pages/AdminTestimonialEditPage'));
const AdminUserDetailPage = lazyPage(() => import('./modules/admin/pages/AdminUserDetailPage'));
const AdminUserEnrollPage = lazyPage(() => import('./modules/admin/pages/AdminUserEnrollPage'));
const AdminInstructorsPage = lazyPage(() => import('./modules/admin/pages/AdminInstructorsPage'));
const AdminInstructorDetailPage = lazyPage(() => import('./modules/admin/pages/AdminInstructorDetailPage'));
const AdminCompliancePage = lazyPage(() => import('./modules/admin/pages/AdminCompliancePage'));
const AdminChatPage = lazyPage(() => import('./modules/admin/pages/AdminChatPage'));
const AdminBillingPage = lazyPage(() => import('./modules/admin/pages/AdminBillingPage'));
const AdminEventsPage = lazyPage(() => import('./modules/admin/pages/AdminEventsPage'));
const AdminEventDetailPage = lazyPage(() => import('./modules/admin/pages/AdminEventDetailPage'));
const AdminEventEditPage = lazyPage(() => import('./modules/admin/pages/AdminEventEditPage'));
const AdminCoursesPage = lazyPage(() => import('./modules/admin/pages/AdminCoursesPage'));
const AdminDiscountsPage = lazyPage(() => import('./modules/admin/pages/AdminDiscountsPage'));
const AdminOrganizationsPage = lazyPage(() => import('./modules/admin/pages/AdminOrganizationsPage'));
const AdminOrganizationDetailPage = lazyPage(() => import('./modules/admin/pages/AdminOrganizationDetailPage'));
const AdminOrgCreationRequestsPage = lazyPage(() => import('./modules/admin/pages/AdminOrgCreationRequestsPage'));
const AdminHostsPage = lazyPage(() => import('./modules/admin/pages/AdminHostsPage'));
const AdminHostDetailPage = lazyPage(() => import('./modules/admin/pages/AdminHostDetailPage'));
const AdminHostEditPage = lazyPage(() => import('./modules/admin/pages/AdminHostEditPage'));
const InstructorEventsPage = lazyPage(() => import('./modules/instructor/pages/InstructorEventsPage'));
const InstructorEventDetailPage = lazyPage(() => import('./modules/instructor/pages/InstructorEventDetailPage'));
const InstructorHostsPage = lazyPage(() => import('./modules/instructor/pages/InstructorHostsPage'));
const InstructorHostDetailPage = lazyPage(() => import('./modules/instructor/pages/InstructorHostDetailPage'));
const AdminVirtualSapiensScenariosPage = lazyPage(() => import('./modules/admin/pages/AdminVirtualSapiensScenariosPage'));
const HubspotSyncPage = lazyPage(() => import('./modules/admin/pages/HubspotSyncPage'));
const ThinkificAnalyticsPage = lazyPage(() => import('./modules/admin/pages/ThinkificAnalyticsPage'));
const AdminInvoicePage = lazyPage(() => import('./modules/invoices/pages/AdminInvoicePage'));
const AdminInvoicesPage = lazyPage(() => import('./modules/invoices/pages/AdminInvoicesPage'));
const InvoicePayPage = lazyPage(() => import('./modules/invoices/pages/InvoicePayPage'));
const AdminOutsidePaymentsPage = lazyPage(() => import('./modules/outsidePayments/pages/AdminOutsidePaymentsPage'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();
  const { pathname, search } = useLocation();

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) {
    // Back to the same page after sign-in: an On-Demand "Enroll" link lands
    // here logged out. stashReturnTo only keeps allowlisted paths.
    stashReturnTo(pathname + search);
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Routes that aren't ready for production traffic yet. In production builds
// (VITE_ENVIRONMENT === 'production') visitors are bounced to /courses; in
// dev/preview builds the page renders normally so the team can keep iterating.
function PreviewRoute({ children }: { children: React.ReactNode }) {
  if (isProduction) return <Navigate to="/courses" replace />;
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();
  const { isLoading: profileLoading } = useProfile();
  const isAdmin = useIsSystemAdmin();

  if (isLoading || (isAuthenticated && profileLoading)) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}

function InstructorRoute({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();
  const { isLoading: profileLoading } = useProfile();
  const isInstructor = useIsInstructor();

  if (isLoading || (isAuthenticated && profileLoading)) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (!isInstructor) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}

// /hub for a visitor who is not signed in yet: park the intent, send them to
// sign in, and LearnerDashboardRoute picks it back up on the way in.
function HubRoute({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) {
    rememberHubVisit();
    return <Navigate to="/?login=1" replace />;
  }

  return <>{children}</>;
}

// Learner dashboard route: authenticated users land here, EXCEPT system
// admins, who are bounced to the admin dashboard. Mirrors AdminRoute so the
// inverse redirect lives in one place rather than inside DashboardPage.
function LearnerDashboardRoute({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();
  const { isLoading: profileLoading } = useProfile();
  const isAdmin = useIsSystemAdmin();

  if (isLoading || (isAuthenticated && profileLoading)) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  // Before the admin bounce: an admin who clicked through to the hub meant it.
  if (takeHubVisit()) return <Navigate to="/hub" replace />;
  if (isAdmin) return <Navigate to="/admin/dashboard" replace />;

  return <>{children}</>;
}

export default function AppRoutes() {
  // Keyed on the path so every navigation gets a fresh boundary. React never
  // hides content that is already on screen behind a fallback, so with one
  // long-lived boundary a page whose code is slow (or never) to arrive left
  // the previous page frozen in place, address bar already changed.
  const { pathname } = useLocation();

  return (
    <ErrorBoundary>
      <Suspense key={pathname} fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/welcome/:slug" element={<ClientLandingPage />} />
          <Route path="/partners/tjx" element={<TjxLandingPage />} />
          <Route path="/partners/walgreens" element={<WalgreensLandingPage />} />
          {/* Public, no-auth co-branded partner offer page — lists upcoming
              sessions for the two Domino's-eligible Level I courses and routes
              to the event checkout with coupon DOMINOS26 pre-applied. */}
          <Route path="/dominos26" element={<DominosLandingPage />} />
          <Route path="/invite" element={<InvitePage />} />
          <Route path="/invite/:token" element={<InviteLinkPage />} />
          <Route path="/invitation" element={<AcceptInvitationPage />} />

          <Route path="/dashboard" element={<LearnerDashboardRoute><DashboardPage /></LearnerDashboardRoute>} />
          <Route path="/get-started" element={<PreviewRoute><GetStartedPage /></PreviewRoute>} />
          <Route path="/courses" element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
          <Route path="/my-courses" element={<ProtectedRoute><MyCoursesPage /></ProtectedRoute>} />
          <Route path="/my-courses/:enrollmentId/assessment" element={<PreviewRoute><EnrollmentAssessmentPage /></PreviewRoute>} />
          <Route path="/my-courses/:enrollmentId/launch" element={<ProtectedRoute><LaunchCoursePage /></ProtectedRoute>} />
          <Route path="/hub" element={<HubRoute><LaunchCoursePage /></HubRoute>} />
          <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
          {/* Public, no-auth event checkout landing — the destination for
              the marketing site's /register/{id}/ "Sign Up to Register"
              button. Renders the event card + Sign Up + Log In CTAs, then
              hands off to /events/:id/register after a successful Auth0
              round-trip (see modules/auth/postAuthReturn.ts). */}
          <Route path="/events/:id/checkout" element={<EventCheckoutPage />} />
          {/* Token-gated invoice pay bridge from the marketing site's
              /invoice/ viewer. Handles its own auth round-trip — a
              ProtectedRoute would bounce to "/" and drop the access_key. */}
          <Route path="/invoices/:id/pay" element={<InvoicePayPage />} />
          <Route path="/practice" element={<ProtectedRoute><PracticePage /></ProtectedRoute>} />
          <Route path="/vs/:id" element={<VsLaunchPage />} />
          <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
          <Route path="/organization" element={<ProtectedRoute><OrganizationPage /></ProtectedRoute>} />
          <Route path="/organization/packages" element={<ProtectedRoute><GroupPackagesPage /></ProtectedRoute>} />
          <Route path="/organization/packages/new" element={<ProtectedRoute><BuyGroupPackagePage /></ProtectedRoute>} />
          <Route path="/organization/analytics" element={<ProtectedRoute><EmployeeAnalyticsPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          {/* Post-payment profile completion: name + shipping address for
              course materials. Reached from the Stripe success handoff
              (useCompleteProfileAfterCheckout) and the global
              ProfileCompletionBanner; also safe to visit directly. */}
          <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfilePage /></ProtectedRoute>} />

          <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
          <Route path="/admin/registrations" element={<AdminRoute><RegistrationsPage /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
          {/* Marketing-site content editing. Static segments before /:slug & /:id. */}
          <Route path="/admin/content" element={<AdminRoute><AdminMarketingContentPage /></AdminRoute>} />
          <Route path="/admin/content/page-editor" element={<AdminRoute><AdminPageContentEditorPage /></AdminRoute>} />
          <Route path="/admin/content/press-releases/new" element={<AdminRoute><AdminPressReleaseEditPage /></AdminRoute>} />
          <Route path="/admin/content/press-releases/:id" element={<AdminRoute><AdminPressReleaseEditPage /></AdminRoute>} />
          <Route path="/admin/content/blog/new" element={<AdminRoute><AdminBlogPostEditPage /></AdminRoute>} />
          <Route path="/admin/content/blog/:id" element={<AdminRoute><AdminBlogPostEditPage /></AdminRoute>} />
          <Route path="/admin/content/portfolio/new" element={<AdminRoute><AdminPortfolioItemEditPage /></AdminRoute>} />
          <Route path="/admin/content/portfolio/:id" element={<AdminRoute><AdminPortfolioItemEditPage /></AdminRoute>} />
          <Route path="/admin/content/testimonials/new" element={<AdminRoute><AdminTestimonialEditPage /></AdminRoute>} />
          <Route path="/admin/content/testimonials/:id" element={<AdminRoute><AdminTestimonialEditPage /></AdminRoute>} />
          <Route path="/admin/content/pages/:slug" element={<AdminRoute><AdminMarketingPageEditPage /></AdminRoute>} />
          {/* Static segment before /:id so it matches ahead of the detail route. */}
          <Route path="/admin/users/:id/enroll" element={<AdminRoute><AdminUserEnrollPage /></AdminRoute>} />
          <Route path="/admin/users/:id" element={<AdminRoute><AdminUserDetailPage /></AdminRoute>} />
          <Route path="/admin/instructors" element={<AdminRoute><AdminInstructorsPage /></AdminRoute>} />
          <Route path="/admin/instructors/:id" element={<AdminRoute><AdminInstructorDetailPage /></AdminRoute>} />
          <Route path="/admin/auditing" element={<AdminRoute><AdminCompliancePage /></AdminRoute>} />
          <Route path="/admin/chat" element={<AdminRoute><AdminChatPage /></AdminRoute>} />
          <Route path="/admin/compliance" element={<Navigate to="/admin/auditing" replace />} />
          <Route path="/admin/billing" element={<AdminRoute><AdminBillingPage /></AdminRoute>} />
          <Route path="/admin/organizations" element={<AdminRoute><AdminOrganizationsPage /></AdminRoute>} />
          <Route path="/admin/organizations/:id" element={<AdminRoute><AdminOrganizationDetailPage /></AdminRoute>} />
          <Route path="/admin/hosts" element={<AdminRoute><AdminHostsPage /></AdminRoute>} />
          {/* Static segments (/new, /:id/edit) before /:id so React Router
              matches them ahead of the dynamic detail route. */}
          <Route path="/admin/hosts/new" element={<AdminRoute><AdminHostEditPage /></AdminRoute>} />
          <Route path="/admin/hosts/:id/edit" element={<AdminRoute><AdminHostEditPage /></AdminRoute>} />
          <Route path="/admin/hosts/:id" element={<AdminRoute><AdminHostDetailPage /></AdminRoute>} />
          <Route path="/admin/webhooks" element={<Navigate to="/admin/auditing" replace />} />
          <Route path="/admin/organization-mergers" element={<Navigate to="/admin/auditing" replace />} />

          <Route path="/instructor/events" element={<InstructorRoute><InstructorEventsPage /></InstructorRoute>} />
          <Route path="/instructor/events/:id" element={<InstructorRoute><InstructorEventDetailPage /></InstructorRoute>} />
          {/* Read-only copy of the admin events list — instructors browse the
              full WZ schedule but can't create / edit / delete or open the
              admin-gated detail page. */}
          <Route path="/instructor/all-events" element={<InstructorRoute><AdminEventsPage readOnly /></InstructorRoute>} />
          {/* /instructor/hosts is no longer in the sidebar (host + venue info
              now lives inline on each event's detail header) but the routes
              stay registered — deep links may exist in old emails/bookmarks. */}
          <Route path="/instructor/hosts" element={<InstructorRoute><InstructorHostsPage /></InstructorRoute>} />
          <Route path="/instructor/hosts/:id" element={<InstructorRoute><InstructorHostDetailPage /></InstructorRoute>} />
          <Route path="/admin/events" element={<AdminRoute><AdminEventsPage /></AdminRoute>} />
          {/* Static segments (/new, /:id/edit) before /:id so React Router
              matches them ahead of the dynamic detail route. */}
          <Route path="/admin/events/new" element={<AdminRoute><AdminEventEditPage /></AdminRoute>} />
          <Route path="/admin/events/:id/edit" element={<AdminRoute><AdminEventEditPage /></AdminRoute>} />
          <Route path="/admin/events/:id" element={<AdminRoute><AdminEventDetailPage /></AdminRoute>} />
          <Route path="/admin/courses" element={<AdminRoute><AdminCoursesPage /></AdminRoute>} />
          <Route path="/admin/discounts" element={<AdminRoute><AdminDiscountsPage /></AdminRoute>} />
          <Route path="/admin/virtual-sapiens" element={<AdminRoute><AdminVirtualSapiensScenariosPage /></AdminRoute>} />
          <Route path="/admin/thinkific" element={<AdminRoute><ThinkificAnalyticsPage /></AdminRoute>} />
          <Route path="/admin/hubspot" element={<AdminRoute><HubspotSyncPage /></AdminRoute>} />
          <Route path="/admin/invoices" element={<AdminRoute><AdminInvoicesPage /></AdminRoute>} />
          <Route path="/admin/outside-payments" element={<AdminRoute><AdminOutsidePaymentsPage /></AdminRoute>} />
          <Route path="/admin/invoices/:id" element={<AdminRoute><AdminInvoicePage /></AdminRoute>} />
          <Route path="/admin/organization-requests" element={<AdminRoute><AdminOrgCreationRequestsPage /></AdminRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
